import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';
import { Spinner } from '@/components/ui/Spinner';
import { ColumnHeader } from '@/features/inventory/components/helpers/ColumnHeader';
import { BillingStatementListItem } from '@/types';

interface BillingTableProps {
  data: BillingStatementListItem[];
  columns: ColumnDef<BillingStatementListItem>[];
  isLoading?: boolean;
  onRowClick?: (row: BillingStatementListItem) => void;
  /**
   * LR-551: with filters applied, "no statements yet" would be a false claim —
   * the month's statements may well exist, just not in this slice.
   */
  isFiltered?: boolean;
  /** LR-557: per-security expansion. The panel renders directly under its row
   * when `isExpanded(row)`; the toggle itself lives in the chevron column. */
  isExpanded?: (row: BillingStatementListItem) => boolean;
  renderExpanded?: (row: BillingStatementListItem) => React.ReactNode;
}

export const BillingTable: React.FC<BillingTableProps> = ({
  data,
  columns,
  isLoading,
  onRowClick,
  isFiltered = false,
  isExpanded,
  renderExpanded,
}) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return <Spinner message="Loading billing statements..." />;
  }

  return (
    <div className="flex flex-1 flex-col overflow-auto min-h-0">
      <div className="flex flex-1 flex-col" style={{ minWidth: 'max-content' }}>
        {/* Table Header — matches CounterpartyTable / TradesTable */}
        <div className="sticky top-0 z-10 flex items-center border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-900">
          {columns.map((column, colIndex) => {
            const columnId =
              ('accessorKey' in column
                ? (column.accessorKey as string)
                : undefined) ||
              column.id ||
              '';
            const width = column.size || 150;
            const headerText =
              typeof column.header === 'string' ? column.header : 'Column';
            return (
              <div
                key={colIndex}
                className="mx-1.5 flex flex-shrink-0 items-center"
                style={{ width: `${width}px`, minWidth: `${width}px` }}
              >
                <ColumnHeader
                  columnId={columnId}
                  headerText={headerText}
                  width={width}
                  sortState={undefined}
                  onSort={() => {}}
                  isLoading={!!isLoading}
                  hasError={false}
                  hoveredColumn={null}
                  onHover={() => {}}
                  align="left"
                />
              </div>
            );
          })}
        </div>

        {/* Table Body */}
        <div className="flex-1">
          {table.getRowModel().rows.map((row) => (
            <React.Fragment key={row.id}>
              <div
                // The row navigates to the document page and, since LR-557, is
                // the only path onto it — so it is an explicit button control
                // with a name, operable from the keyboard (WCAG 2.1.1) and
                // announced as such (WCAG 4.1.2). role + aria-label are what a
                // bare focusable div lacked; TradesTable rows are mouse-only, so
                // this is deliberately more than they carry. The `closest` guard
                // plus each nested control's own stopPropagation keep the
                // chevron / Mark paid / menu items from also navigating.
                role="button"
                aria-label={`Open billing document for ${row.original.counterparty_name} ${row.original.currency}`}
                tabIndex={0}
                className="flex items-center border-b border-gray-200 px-4 py-2 hover:bg-gray-50 transition-colors cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-400 focus-visible:outline-offset-[-2px]"
                onClick={(e) => {
                  if (
                    (e.target as HTMLElement).closest(
                      'button, a, input, [role="menuitem"]',
                    )
                  )
                    return;
                  onRowClick?.(row.original);
                }}
                onKeyDown={(e) => {
                  // Nested controls keep their own keys (the chevron is a real
                  // button); only act when the row itself is focused.
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRowClick?.(row.original);
                  }
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const width = cell.column.columnDef.size || 150;
                  return (
                    <div
                      key={cell.id}
                      className="mx-1.5 flex flex-shrink-0 items-center text-sm text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap"
                      style={{ width: `${width}px`, minWidth: `${width}px` }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  );
                })}
              </div>
              {isExpanded?.(row.original) && renderExpanded?.(row.original)}
            </React.Fragment>
          ))}
          {data.length === 0 && (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500">
              {isFiltered
                ? 'No billing statements match these filters.'
                : 'No billing statements yet. Statements are generated on the 1st of each month from accrued daily entries.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
